import React, { useCallback } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { SOFT_SIGNALS, SoftSignal } from 'models/Lead';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import UnsureSignalModal from '../UnsureSignalModal';
import DislikeSignalModal from '../DislikeSignalModal';
import api from 'api';
import { useTranslation } from 'react-i18next';
import SoftSignalIcon from 'components/SoftSignalIcon';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { SoftSignalInitiatedEventProps } from 'models/MixpanelEvents';

import './SoftSignals.styles.scss';
import './SoftSignals.styles.responsive.scss';

type SoftSignalsProps = {
  className?: string;
  dataRoomId: string;
  dataRoomNodeId: string;
  dealRoomId: string;
  lastSoftSignal?: SoftSignal;
  onSuccess: (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => void;
};

const SoftSignals: React.FC<SoftSignalsProps> = (props) => {
  const {
    className,
    dataRoomNodeId,
    lastSoftSignal,
    dataRoomId,
    dealRoomId,
    onSuccess,
  } = props;

  const [unsureModal, unsureModalRef] = useCallbackRef<ModalRef>();
  const [dislikeModal, dislikeModalRef] = useCallbackRef<ModalRef>();

  const classes = classNames('soft-signals', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const renderButton = useCallback(
    (softSignal: SoftSignal, onClick: () => void) => {
      const applied = !!lastSoftSignal && softSignal === lastSoftSignal;
      const dimmed = !!lastSoftSignal && softSignal !== lastSoftSignal;

      return (
        <Button
          variant="icon"
          className={classNames(softSignal, {
            applied,
            dimmed,
          })}
          onClick={() => {
            if (applied) return;
            onClick();
          }}
        >
          <SoftSignalIcon softSignal={softSignal} transparent />
        </Button>
      );
    },
    [lastSoftSignal],
  );

  const onLike = useCallback(() => {
    const softSignal = SOFT_SIGNALS.LIKE;

    mixpanelService.softSignalInitiated(
      getMixpanelProps({
        dataRoomId,
        documentId: dataRoomNodeId,
        dealId: dealRoomId,
        reaction: t('interested'),
      }) as SoftSignalInitiatedEventProps,
    );

    api.dataRoom.nodes.softSignals(dataRoomNodeId, {
      softSignal,
    });

    onSuccess(softSignal);
  }, [dataRoomNodeId, dataRoomId, dealRoomId, getMixpanelProps, onSuccess, t]);

  return (
    <div className={classes}>
      <p>{t('softSignalText')}</p>

      <div>
        {renderButton(SOFT_SIGNALS.LIKE, onLike)}
        {renderButton(SOFT_SIGNALS.UNSURE, unsureModal?.open)}
        {renderButton(SOFT_SIGNALS.DISLIKE, dislikeModal?.open)}
      </div>

      <UnsureSignalModal
        ref={unsureModalRef}
        dataRoomNodeId={dataRoomNodeId}
        dataRoomId={dataRoomId}
        dealRoomId={dealRoomId}
        onSuccess={onSuccess}
      />
      <DislikeSignalModal
        ref={dislikeModalRef}
        dataRoomNodeId={dataRoomNodeId}
        dataRoomId={dataRoomId}
        dealRoomId={dealRoomId}
        onSuccess={onSuccess}
      />
    </div>
  );
};

export default SoftSignals;
